import axios from 'axios';
import { clearStoredUser, getStoredUser, setStoredUser } from './utils/safeLocalStorage';
import { getCalculatorErrorMessage, normalizeCalculatorApiError, readCalculatorBlobText } from './utils/calculatorInputs';

export const API_BASE_URL = process.env.VITE_API_URL || 'https://api-g2ovfwkvva-uc.a.run.app';

// Shared instance for authenticated requests — uses httpOnly cookie, no token in headers
const authInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
});

// Track active requests for cancellation
const activeRequests = new Map();

const buildRequestKey = (config) => {
    const method = config.method || 'get';
    const url = config.url || '';
    const params = config.params ? JSON.stringify(config.params) : '';
    return `${method}-${url}-${params}`;
};

const removeTrackedRequest = (config) => {
    const requestId = config?.metadata?.requestId;
    if (!requestId) return;

    const entry = activeRequests.get(requestId.key);
    if (!entry || entry.id !== requestId.id) return;
    activeRequests.delete(requestId.key);
};

// Request interceptor to track requests
authInstance.interceptors.request.use((config) => {
    const cancelToken = axios.CancelToken.source();
    config.cancelToken = cancelToken.token;

    // Track by method+url+params and assign a unique id to avoid collisions on rapid repeats.
    const requestKey = buildRequestKey(config);
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    config.metadata = {
        ...(config.metadata || {}),
        requestId: {
            key: requestKey,
            id: requestId,
        },
    };

    activeRequests.set(requestKey, { cancelToken, timestamp: Date.now(), id: requestId });

    return config;
});

// Redirect to login on 401 (expired/invalid token) or 403 (no token — stale pre-migration session)
authInstance.interceptors.response.use(
    (response) => {
        removeTrackedRequest(response.config);
        return response;
    },
    async (error) => {
        removeTrackedRequest(error.config);
        await normalizeCalculatorApiError(error);
        if (error.response?.status === 401 || error.response?.status === 403) {
            clearStoredUser();
            localStorage.removeItem('selectedProperty');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Export function to cancel all pending requests (call on component unmount)
export const cancelPendingRequests = () => {
    activeRequests.forEach(({ cancelToken }) => {
        try {
            cancelToken.cancel('Request cancelled due to component unmount');
        } catch (err) {
            // Ignore errors from already completed requests
        }
    });
    activeRequests.clear();
};

// Cleanup old requests periodically (every 5 minutes)
setInterval(() => {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes
    for (const [key, { timestamp }] of activeRequests.entries()) {
        if (now - timestamp > maxAge) {
            activeRequests.delete(key);
        }
    }
}, 60000);

// Shared instance for public (unauthenticated) requests
const publicInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
});

const login = async (formState) => {
    const response = await publicInstance.post(`/login`, {
        email: formState.email,
        password: formState.password
    });
    // If 2FA is required the server returns { requires2FA: true } — don't store anything yet
    if (response.data?.requires2FA) return response.data;
    setStoredUser(response.data);
    return response.data;
};

const verifyTOTPLogin = async (token) => {
    const response = await publicInstance.post('/verifyTOTPLogin', { token });
    setStoredUser(response.data);
    return response.data;
};

const getTOTPStatus = async () => {
    const response = await authInstance.get('/getTOTPStatus');
    return response.data;
};

const setupTOTP = async () => {
    const response = await authInstance.post('/setupTOTP');
    return response.data;
};

const enableTOTP = async (token) => {
    const response = await authInstance.post('/enableTOTP', { token });
    return response.data;
};

const disableTOTP = async (password) => {
    const response = await authInstance.post('/disableTOTP', { password });
    return response.data;
};

const logoutUser = async () => {
    await publicInstance.post(`/logout`);
};


const addCalculationData = async (values, property) => {
    const response = await authInstance.post(`/calculation/addCalculationData`, {
        property: {
            propertyID: property,
        },
        calculationData: {
            unitPrice: values.unitPrice,
            co2UK: values.co2UK,
            exportPrice: values.exportPrice,
            costPerWatt: {
                tile: values.costPerWatt.tile,
                slate: values.costPerWatt.slate,
                trapezoidal: values.costPerWatt.trapezoidal,
                corrugated: values.costPerWatt.corrugated,
                standingSeam: values.costPerWatt.standingSeam,
                kingspan: values.costPerWatt.kingspan,
                vdvFlatRoof: values.costPerWatt.vdvFlatRoof,
                ground: values.costPerWatt.ground,
            },
            panelSize: values.panelSize,
            inverter: {
                standard: values.inverter.standard,
                hybrid: values.inverter.hybrid,
            },
            dcComponents: values.dcComponents,
            acComponents: values.acComponents,
            paperwork: {
                mcs: values.paperwork.mcs,
                buildControl: values.paperwork.buildControl,
            },
            addOns: {
                eddie: values.addOns.eddie,
                zappie: values.addOns.zappie,
                firstBattery: values.addOns.firstBattery,
                secondBattery: values.addOns.secondBattery,
            },
            minPanels: values.minPanels,
            maxPanels: values.maxPanels,
            modulePricePerWatt: values.modulePricePerWatt,
            labour: {
                roofing: values.labour.roofing,
                electrical: values.labour.electrical,
            },
            margin: values.margin,
            scaffolding: values.scaffolding,
            vat: values.vat,
        }
    })
    return response;
}

const getCalculationData = async (property) => {
    const queryString = new URLSearchParams({ property });
    const response = await authInstance.get(`/calculation/getCalculationData?${queryString}`);
    return response.data;
};

const getPropertyConfig = async (property) => {
    const queryString = new URLSearchParams({ property });
    const response = await authInstance.get(`/property/getPropertyConfig?${queryString}`);
    return response.data;
};

const getWebsiteDemoConfig = async (market) => {
    const response = await authInstance.get('/website/demo-config', { params: { market } });
    return response.data;
};

const updateWebsiteDemoConfig = async (config) => {
    const response = await authInstance.put('/website/demo-config', config);
    return response.data;
};

const getRoofAssistMetrics = async (propertyID) => {
    const queryString = new URLSearchParams({ propertyID });
    const response = await authInstance.get(`/property/getRoofAssistMetrics?${queryString}`);
    return response.data;
};

const getRoofAssistTrend = async (propertyID, days = 30) => {
    const queryString = new URLSearchParams({ propertyID, days: String(days) });
    const response = await authInstance.get(`/property/getRoofAssistTrend?${queryString}`);
    return response.data;
};

const updatePdfContent = async (property, pdfContent) => {
    const queryString = new URLSearchParams({ property });
    const response = await authInstance.put(`/pdf/updatePdfContent?${queryString}`, pdfContent);
    return response.data;
};

const updatePdfLayout = async (property, pdfLayout) => {
    const queryString = new URLSearchParams({ property });
    const response = await authInstance.put(`/pdf/updatePdfLayout?${queryString}`, pdfLayout);
    return response.data;
};

const updatePdfCanvasLayout = async (property, canvasLayout) => {
    const queryString = new URLSearchParams({ property });
    const response = await authInstance.put(`/pdf/updatePdfCanvasLayout?${queryString}`, canvasLayout);
    return response.data;
};

const normalizePropertyList = (payload) => {
    const rawList = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data) ? payload.data : []);

    return rawList
        .map((item) => {
            if (!item || typeof item !== 'object') return null;

            const propertyID = item.propertyID || item.id || item._id || item.propertyId || '';
            const companyName = item.companyName || item.name || item.title || propertyID;

            if (!propertyID) return null;

            return {
                ...item,
                propertyID,
                companyName,
            };
        })
        .filter(Boolean);
};

const getPropertyList = async () => {
    const response = await authInstance.get(`/property/getPropertyList`);
    return normalizePropertyList(response.data);
};

const getManagedUsers = async () => {
    const response = await authInstance.get('/quotes/users');
    return Array.isArray(response.data?.users) ? response.data.users : [];
};

const updateManagedUserProperties = async (userId, properties) => {
    const response = await authInstance.patch(`/quotes/users/${encodeURIComponent(userId)}`, {
        propertyID: properties[0],
        properties,
    });
    return response.data?.user;
};

const addNewUser = async (userData) => {
    const response = await authInstance.post(`/signup`, {
        newUserData: {
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            property: userData.property,
            role: userData.role,
            ...(userData.capabilities && { capabilities: userData.capabilities }),
        }
    });
    return response;
}

const updatePasswordOnFirstLogin = async (email, newPassword, confirmPassword) => {
    const response = await publicInstance.post('/updatePasswordOnFirstLogin', {
        email,
        newPassword,
        confirmPassword
    });
    return response.data;
};

const changePassword = async (email, currentPassword, newPassword, confirmPassword) => {
    const response = await publicInstance.post('/changePassword', {
        email,
        currentPassword,
        newPassword,
        confirmPassword
    });
    return response.data;
};

const requestPasswordReset = async (email) => {
    const response = await publicInstance.post('/requestPasswordReset', { email });
    return response.data;
};

const resetPassword = async (resetToken, newPassword, confirmPassword) => {
    const response = await publicInstance.post('/resetPassword', {
        resetToken,
        newPassword,
        confirmPassword
    });
    return response.data;
};

const getCustomCostItems = async (propertyID) => {
    const response = await authInstance.get('/costs/getCustomCostItems', { params: { propertyID } });
    return response.data;
};

const addCustomCostItem = async (propertyID, name, description, cost, includeInClientResponse) => {
    const user = getStoredUser();
    const { email, role } = user;

    const response = await authInstance.post('/costs/addCustomCostItem', {
        propertyID,
        name,
        description,
        cost,
        includeInClientResponse: includeInClientResponse || false,
        createdByEmail: email,
        createdByRole: role
    });
    return response.data;
};

const updateCustomCostItem = async (itemID, name, description, cost, includeInClientResponse) => {
    const response = await authInstance.put(`/costs/updateCustomCostItem/${itemID}`, {
        name,
        description,
        cost,
        includeInClientResponse: includeInClientResponse || false
    });
    return response.data;
};

const deleteCustomCostItem = async (itemID) => {
    const response = await authInstance.delete(`/costs/deleteCustomCostItem/${itemID}`);
    return response.data;
};

// Cost Variable API calls
const getCostVariables = async (propertyID, category = null) => {
    const params = { propertyID };
    if (category) params.category = category;
    const response = await authInstance.get('/costs/getCostVariables', { params });
    const rawItems = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.data) ? response.data.data : []);

    return rawItems
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
            ...item,
            name: item.name || item.key || 'Unnamed Variable',
            key: item.key || item.name || '',
            type: typeof item.type === 'string' ? item.type : 'fixed',
            value: Number.isFinite(Number(item.value)) ? Number(item.value) : 0,
            category: item.category || 'uncategorized',
            displayUnit: item.displayUnit || '£',
        }));
};

const addCostVariable = async (propertyID, name, description, type, value, formula, category, key, displayUnit) => {
    const user = getStoredUser();
    const { email, role } = user;

    const response = await authInstance.post('/costs/addCostVariable', {
        propertyID,
        name,
        description,
        type,
        value,
        formula,
        category,
        key,
        displayUnit: displayUnit || '$',  // Use generic symbol as last resort only
        createdByEmail: email,
        createdByRole: role
    });
    return response.data;
};

const updateCostVariable = async (variableID, name, description, type, value, formula, category, displayUnit, isActive) => {
    const response = await authInstance.put(`/costs/updateCostVariable/${variableID}`, {
        name,
        description,
        type,
        value,
        formula,
        category,
        displayUnit,
        isActive
    });
    return response.data;
};

const deleteCostVariable = async (variableID) => {
    const response = await authInstance.delete(`/costs/deleteCostVariable/${variableID}`);
    return response.data;
};

// Calculation Rule API calls
const getCalculationRules = async (propertyID) => {
    const response = await authInstance.get('/rules/getCalculationRules', { params: { propertyID } });
    return response.data;
};

const getActiveCalculationRule = async (propertyID) => {
    const response = await authInstance.get('/rules/getActiveCalculationRule', { params: { propertyID } });
    return response.data;
};

const createCalculationRule = async (propertyID, name, description, ruleChain, phases, calculationOrder) => {
    const user = getStoredUser();
    const { email, role } = user;

    const response = await authInstance.post('/rules/createCalculationRule', {
        propertyID,
        name,
        description,
        ruleChain,
        phases,
        calculationOrder,
        createdByEmail: email,
        createdByRole: role
    });
    return response.data;
};

const updateCalculationRule = async (ruleID, name, description, ruleChain, phases, calculationOrder, isActive) => {
    const response = await authInstance.put(`/rules/updateCalculationRule/${ruleID}`, {
        name,
        description,
        ruleChain,
        phases,
        calculationOrder,
        isActive
    });
    return response.data;
};

const deleteCalculationRule = async (ruleID) => {
    const response = await authInstance.delete(`/rules/deleteCalculationRule/${ruleID}`);
    return response.data;
};

// Variable Group API calls
const getVariableGroups = async (propertyID) => {
    const response = await authInstance.get('/variables/getVariableGroups', { params: { propertyID } });
    const rawItems = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.data) ? response.data.data : []);

    return rawItems
        .filter((item) => item && typeof item === 'object')
        .map((item, index) => ({
            ...item,
            category: item.category || `group_${index + 1}`,
            name: item.name || item.category || `Group ${index + 1}`,
        }));
};

const createVariableGroup = async (propertyID, name, category, description, treatmentType, color) => {
    const user = getStoredUser();
    const { email, role } = user;

    const response = await authInstance.post('/variables/createVariableGroup', {
        propertyID,
        name,
        category,
        description,
        treatmentType,
        color,
        createdByEmail: email,
        createdByRole: role
    });
    return response.data;
};

const updateVariableGroup = async (groupID, name, description, treatmentType, order, color, isActive) => {
    const response = await authInstance.put(`/variables/updateVariableGroup/${groupID}`, {
        name,
        description,
        treatmentType,
        order,
        color,
        isActive
    });
    return response.data;
};

const deleteVariableGroup = async (groupID) => {
    const response = await authInstance.delete(`/variables/deleteVariableGroup/${groupID}`);
    return response.data;
};


const getUploadSignature = async (propertyID, purpose = 'property-image') => {
    const response = await authInstance.post('/upload/generateUploadSignature', { propertyID, purpose });
    return response.data;
};

const deleteImageFromCloudinary = async (propertyID, publicId) => {
    const response = await authInstance.post('/upload/deleteImage', { propertyID, publicId });
    return response.data;
};

const updatePropertyImages = async (propertyID, images) => {
    const response = await authInstance.put(`/property/updatePropertyImages?property=${propertyID}`, { images });
    return response.data;
};

const updateEmailConfig = async (propertyID, emailConfig) => {
    const response = await authInstance.put(`/property/updateEmailConfig?property=${propertyID}`, emailConfig);
    return response.data;
};

const getDashboardData = async () => {
    const response = await authInstance.get('/analytics/getDashboardData');
    return response.data;
};

const trackAdminEvent = async ({ eventName, eventData = {}, timestamp = new Date().toISOString(), userAgent, url } = {}) => {
    const payload = {
        eventName,
        eventData,
        timestamp,
    };

    if (userAgent) payload.userAgent = userAgent;
    if (url) payload.url = url;

    const response = await authInstance.post('/analytics/track-event-admin', payload);
    return response.data;
};

const getEventStats = async ({ eventName, eventNames, propertyID, url, startDate, endDate, groupBy, includeLocal = false } = {}) => {
    const params = {};
    if (Array.isArray(eventNames) && eventNames.length > 0) {
        params.eventNames = eventNames;
    } else if (eventName) {
        params.eventName = eventName;
    }
    if (propertyID) params.propertyID = propertyID;
    if (url) params.url = url;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (groupBy) params.groupBy = groupBy;
    if (includeLocal) params.includeLocal = true;
    const response = await authInstance.get('/analytics/getEventStats', { params });
    return response.data;
};

export const getJourneyFunnel = async ({ propertyID, periodKey } = {}) => {
    const response = await authInstance.get('/analytics/journey-funnel', {
        params: { propertyID, periodKey },
    });
    return response.data;
};

const getOutreachDiscoveryRuns = async ({ limit = 100, status, countyOrArea, country } = {}) => {
    const params = { limit };
    if (status) params.status = status;
    if (countyOrArea) params.countyOrArea = countyOrArea;
    if (country) params.country = country;
    const response = await authInstance.get('/analytics/outreach-discovery/runs', { params });
    return response.data;
};

const getOutreachEmailEvents = async ({ limit = 100, eventType, recipient, startDate, endDate } = {}) => {
    const params = { limit };
    if (eventType) params.eventType = eventType;
    if (recipient) params.recipient = recipient;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await authInstance.get('/analytics/outreach-email-events', { params });
    return response.data;
};

const getOutreachEmailEngagement = async ({ limit = 200, startDate, endDate } = {}) => {
    const params = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await authInstance.get('/analytics/outreach-email-engagement', { params });
    return response.data;
};

const getOutreachWarmLeads = async ({ limit = 200, minimumScore = 40, startDate, endDate } = {}) => {
    const params = { limit, minimumScore };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await authInstance.get('/analytics/outreach-warm-leads', { params });
    return response.data;
};

const markWarmLeadDoNotSend = async ({ recipient } = {}) => {
    const response = await authInstance.patch(
        `/analytics/outreach-warm-leads/${encodeURIComponent(recipient)}/do-not-send`
    );
    return response.data;
};

const getOutreachProspects = async ({ limit = 200, country, countyOrArea, search, contacted } = {}) => {
    const params = { limit };
    if (country) params.country = country;
    if (countyOrArea) params.countyOrArea = countyOrArea;
    if (search) params.search = search;
    if (contacted === true || contacted === false) params.contacted = contacted;
    const response = await authInstance.get('/analytics/outreach-prospects', { params });
    return response.data;
};

const getOutreachProspectTimeline = async ({ prospectId } = {}) => {
    const response = await authInstance.get(`/analytics/outreach-prospects/${encodeURIComponent(prospectId)}/timeline`);
    return response.data;
};

const bulkUpdateOutreachProspects = async ({ prospectIds, doNotSend, marketingBasis } = {}) => {
    const payload = { prospectIds };
    if (doNotSend === true || doNotSend === false) payload.doNotSend = doNotSend;
    if (marketingBasis) payload.marketingBasis = marketingBasis;
    const response = await authInstance.patch('/analytics/outreach-prospects/bulk-contact', payload);
    return response.data;
};

const sendManualOutreachFollowUp = async ({ prospectId } = {}) => {
    const response = await authInstance.post(`/analytics/outreach-prospects/${encodeURIComponent(prospectId)}/send-follow-up`);
    return response.data;
};

const updateOutreachProspectDoNotSend = async ({ prospectId, doNotSend }) => {
    const response = await authInstance.patch(
        `/analytics/outreach-prospects/${encodeURIComponent(prospectId)}/do-not-send`,
        { doNotSend }
    );
    return response.data;
};

const updateOutreachProspectMarketingBasis = async ({ prospectId, marketingBasis }) => {
    const response = await authInstance.patch(
        `/analytics/outreach-prospects/${encodeURIComponent(prospectId)}/marketing-basis`,
        { marketingBasis }
    );
    return response.data;
};

const getSolmatixEmailSummary = async ({ startDate, endDate } = {}) => {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await authInstance.get('/analytics/solmatix-email-summary', { params });
    return response.data;
};

const getOutreachDiagnostics = async () => {
    const response = await authInstance.get('/analytics/outreach-diagnostics');
    return response.data;
};

const getOutreachPerformance = async ({ days = 30 } = {}) => {
    const response = await authInstance.get('/analytics/outreach-performance', { params: { days } });
    return response.data;
};

const recordOutreachOutcome = async ({
    recipient,
    outcome,
    campaignId,
    templateId,
    sourceKey,
    occurredAt,
} = {}) => {
    const response = await authInstance.post('/analytics/outreach-outcomes', {
        recipient,
        outcome,
        campaignId,
        templateId,
        sourceKey,
        occurredAt,
    });
    return response.data;
};

const triggerOutreachDiscoveryRun = async ({
    country,
    region,
    runLabel,
    seedUrl,
    seedUrls,
    listingLinkPattern,
    listingLinkPatterns,
    appendTo,
} = {}) => {
    const payload = { country, region };
    if (runLabel) payload.runLabel = runLabel;
    if (seedUrl) payload.seedUrl = seedUrl;
    if (Array.isArray(seedUrls) && seedUrls.length > 0) payload.seedUrls = seedUrls;
    if (listingLinkPattern) payload.listingLinkPattern = listingLinkPattern;
    if (Array.isArray(listingLinkPatterns) && listingLinkPatterns.length > 0) payload.listingLinkPatterns = listingLinkPatterns;
    if (appendTo) payload.appendTo = appendTo;
    const response = await authInstance.post('/analytics/outreach-discovery/trigger', payload);
    return response.data;
};

const cancelOutreachDiscoveryRun = async ({ runId } = {}) => {
    const payload = { runId };
    const response = await authInstance.post('/analytics/outreach-discovery/cancel', payload);
    return response.data;
};

const cleanupSuppressedOutreachRows = async ({ dryRun = true, csvPaths } = {}) => {
    const payload = { dryRun };
    if (Array.isArray(csvPaths) && csvPaths.length > 0) payload.csvPaths = csvPaths;
    const response = await authInstance.post('/analytics/outreach-discovery/cleanup-suppressed', payload);
    return response.data;
};

const runScheduledOutreachDiscoveryStep = async () => {
    const response = await authInstance.post('/analytics/outreach-discovery/run-next');
    return response.data;
};

const processOutreachDiscoveryBatch = async () => {
    const response = await authInstance.post('/analytics/outreach-discovery/process-batch');
    return response.data;
};

const runScheduledWarmLeadFollowUps = async () => {
    const response = await authInstance.post('/analytics/outreach-warm-leads/run-scheduled');
    return response.data;
};

const getOutreachSuppressions = async () => {
    const response = await authInstance.get('/analytics/outreach-suppressions');
    return response.data;
};

const getOutreachApprovedProspects = async () => {
    const response = await authInstance.get('/analytics/outreach-approved-prospects');
    return response.data;
};

const getOutreachAutomationRuns = async ({ limit = 100 } = {}) => {
    const response = await authInstance.get('/analytics/outreach-automation-runs', { params: { limit } });
    return response.data;
};

const getOutreachDeliveryControl = async () => {
    const response = await authInstance.get('/analytics/outreach-delivery-control');
    return response.data;
};

const updateOutreachDeliveryControl = async ({ mode, reason } = {}) => {
    const response = await authInstance.post('/analytics/outreach-delivery-control', { mode, reason });
    return response.data;
};

const retryFailedOutreachFollowUps = async ({ lookbackDays = 7, limit = 20 } = {}) => {
    const response = await authInstance.post('/analytics/outreach-follow-ups/retry-failed', { lookbackDays, limit });
    return response.data;
};

const toBoundedInteger = (value, fallback, minimum, maximum) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return fallback;
    return Math.min(maximum, Math.max(minimum, Math.trunc(numericValue)));
};

const triggerWarmLeadFollowUps = async ({ minimumScore = 40, limit = 20, dryRun = false, followUpWindowDays = 7 } = {}) => {
    const payload = {
        minimumScore: toBoundedInteger(minimumScore, 40, 0, 200),
        limit: toBoundedInteger(limit, 20, 1, 50),
        dryRun: dryRun === true,
        followUpWindowDays: toBoundedInteger(followUpWindowDays, 7, 1, 90),
    };
    const response = await authInstance.post('/analytics/outreach-warm-leads/follow-up', payload);
    return response.data;
};

const sendOutreachPreviewEmails = async ({ recipient } = {}) => {
    const payload = { recipient };
    const response = await authInstance.post('/analytics/outreach-test-emails', payload);
    return response.data;
};

const getOutreachPreviewEmails = async ({ recipient } = {}) => {
    const response = await authInstance.get('/analytics/outreach-test-emails/preview', {
        params: { recipient },
    });
    return response.data;
};

const sendMonthlyUsageReportTest = async ({ propertyID, testRecipient, referenceDate } = {}) => {
    const payload = { propertyID };
    if (testRecipient) payload.testRecipient = testRecipient;
    if (referenceDate) payload.referenceDate = referenceDate;

    const response = await authInstance.post('/analytics/test-monthly-usage-report', payload);
    return response.data;
};

const getPdfVersionHistory = async (property, type) => {
    const response = await authInstance.get('/pdf/getPdfVersionHistory', { params: { property, type } });
    return response.data;
};

const getPdfVersion = async (id) => {
    const response = await authInstance.get(`/pdf/getPdfVersion/${id}`);
    return response.data;
};

const restorePdfVersion = async (id) => {
    const response = await authInstance.post(`/pdf/restorePdfVersion/${id}`);
    return response.data;
};

const getSolarData = async ({ data, property, calculationMethod = 'pvgis' }) => {
    const response = await publicInstance.post('/solar-data/getSolarData', { data, property, calculationMethod });
    return response.data;
};

const getCalculationBreakdown = async ({ data, property, calculationMethod = 'pvgis' }) => {
    const response = await authInstance.post('/solar-data/getCalculationBreakdown', { data, property, calculationMethod });
    return response.data;
};

const getAuditLogs = async ({ property, limit = 100, offset = 0 } = {}) => {
    const params = { limit, offset };
    if (property) params.property = property;
    const response = await authInstance.get('/analytics/getAuditLogs', { params });
    return response.data;
};

const getLeads = async ({ propertyID, market, state, matchType, utility, importMin, importMax, exportMin, exportMax, limit, offset } = {}) => {
    const params = { propertyID };
    if (market) params.market = market;
    if (state) params.state = state;
    if (matchType) params.matchType = matchType;
    if (utility) params.utility = utility;
    if (importMin !== undefined && importMin !== null) params.importMin = importMin;
    if (importMax !== undefined && importMax !== null) params.importMax = importMax;
    if (exportMin !== undefined && exportMin !== null) params.exportMin = exportMin;
    if (exportMax !== undefined && exportMax !== null) params.exportMax = exportMax;
    if (limit !== undefined && limit !== null) params.limit = limit;
    if (offset !== undefined && offset !== null) params.offset = offset;

    const response = await authInstance.get('/leads/getLeads', { params });
    if (Array.isArray(response.data)) {
        return { leads: response.data, total: response.data.length, limit: response.data.length, offset: 0 };
    }
    return {
        leads: Array.isArray(response.data?.leads) ? response.data.leads : [],
        total: Number.isFinite(Number(response.data?.total)) ? Number(response.data.total) : 0,
        limit: Number.isFinite(Number(response.data?.limit)) ? Number(response.data.limit) : 0,
        offset: Number.isFinite(Number(response.data?.offset)) ? Number(response.data.offset) : 0,
    };
};

const previewPDF = async (propertyID) => {
    const response = await authInstance.get('/pdf/previewPDF', {
        params: { property: propertyID },
        responseType: 'blob',
    });
    if (!(response.data instanceof Blob) || !(await readCalculatorBlobText(response.data.slice(0, 5))).startsWith('%PDF-')) {
        const error = new Error('PDF preview unavailable');
        error.response = { status: 502, data: response.data };
        await normalizeCalculatorApiError(error);
        error.friendlyMessage = getCalculatorErrorMessage(error, 'We cannot generate the PDF preview. Please check the current calculator settings and try again.');
        throw error;
    }
    return response.data;
};

const withOptional = (payload, key, value) => {
    if (value !== undefined) payload[key] = value;
    return payload;
};

const normalizeOptionalString = (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
};

const createProperty = async ({ propertyID, companyName, active, details, currency, installationCostRounding, calculationMethod, avgAnnualConsumption, features, branding, calculatorUI, market, addressMode, utilityRateDefaults, locale, co2Factor }) => {
    const normalizedPropertyId = normalizeOptionalString(propertyID);
    const normalizedCompanyName = typeof companyName === 'string' ? companyName.trim() : companyName;
    const normalizedCurrency = normalizeOptionalString(currency);
    const normalizedCalculationMethod = normalizeOptionalString(calculationMethod);
    const payload = {
        companyName: normalizedCompanyName,
        active,
        details,
        currency: normalizedCurrency,
        calculationMethod: normalizedCalculationMethod,
        avgAnnualConsumption,
        features,
        branding,
        calculatorUI,
    };
    if (normalizedPropertyId) payload.propertyID = normalizedPropertyId;
    withOptional(payload, 'market', normalizeOptionalString(market));
    withOptional(payload, 'addressMode', normalizeOptionalString(addressMode));
    withOptional(payload, 'utilityRateDefaults', utilityRateDefaults);
    withOptional(payload, 'locale', normalizeOptionalString(locale));
    withOptional(payload, 'co2Factor', co2Factor);
    withOptional(payload, 'installationCostRounding', installationCostRounding);
    const response = await authInstance.post('/property/createProperty', payload);
    return response.data;
};

const updatePropertyDetails = async ({ currentPropertyID, propertyID, companyName, active, details, currency, installationCostRounding, calculationMethod, avgAnnualConsumption, features, branding, calculatorUI, locale, co2Factor, market, addressMode, utilityRateDefaults }) => {
    const normalizedCurrentPropertyID = typeof currentPropertyID === 'string' ? currentPropertyID.trim() : currentPropertyID;
    const normalizedPropertyID = typeof propertyID === 'string' ? propertyID.trim() : propertyID;
    const normalizedCompanyName = typeof companyName === 'string' ? companyName.trim() : companyName;
    const normalizedCurrency = normalizeOptionalString(currency);
    const normalizedCalculationMethod = normalizeOptionalString(calculationMethod);
    const normalizedLocale = normalizeOptionalString(locale);
    const payload = {
        currentPropertyID: normalizedCurrentPropertyID,
        propertyID: normalizedPropertyID,
        companyName: normalizedCompanyName,
        active,
        details,
        currency: normalizedCurrency,
        calculationMethod: normalizedCalculationMethod,
        avgAnnualConsumption,
        features,
        branding,
        calculatorUI,
        locale: normalizedLocale,
        co2Factor,
    };
    withOptional(payload, 'market', normalizeOptionalString(market));
    withOptional(payload, 'addressMode', normalizeOptionalString(addressMode));
    withOptional(payload, 'utilityRateDefaults', utilityRateDefaults);
    withOptional(payload, 'installationCostRounding', installationCostRounding);
    const response = await authInstance.put('/property/updatePropertyDetails', payload);
    return response.data;
};

const deleteProperty = async (propertyID) => {
    const response = await authInstance.delete(`/property/deleteProperty/${propertyID}`);
    return response.data;
};

const checkServerHealth = async () => {
    return publicInstance.get('/health', { timeout: 5000 });
};

export { addCalculationData, login, logoutUser, verifyTOTPLogin, getTOTPStatus, setupTOTP, enableTOTP, disableTOTP, getAuditLogs, getLeads, getDashboardData, trackAdminEvent, getEventStats, getOutreachDiscoveryRuns, getOutreachEmailEvents, getOutreachEmailEngagement, getOutreachWarmLeads, markWarmLeadDoNotSend, getOutreachProspects, getOutreachProspectTimeline, bulkUpdateOutreachProspects, sendManualOutreachFollowUp, updateOutreachProspectDoNotSend, updateOutreachProspectMarketingBasis, getSolmatixEmailSummary, getOutreachDiagnostics, getOutreachPerformance, recordOutreachOutcome, triggerOutreachDiscoveryRun, cancelOutreachDiscoveryRun, cleanupSuppressedOutreachRows, runScheduledOutreachDiscoveryStep, processOutreachDiscoveryBatch, runScheduledWarmLeadFollowUps, getOutreachSuppressions, getOutreachApprovedProspects, getOutreachAutomationRuns, getOutreachDeliveryControl, updateOutreachDeliveryControl, retryFailedOutreachFollowUps, triggerWarmLeadFollowUps, getOutreachPreviewEmails, sendOutreachPreviewEmails, sendMonthlyUsageReportTest, getSolarData, getCalculationBreakdown, getCalculationData, getPropertyConfig, getWebsiteDemoConfig, updateWebsiteDemoConfig, getRoofAssistMetrics, getRoofAssistTrend, updatePdfContent, updatePdfLayout, updatePdfCanvasLayout, addNewUser, getPropertyList, getManagedUsers, updateManagedUserProperties, updatePasswordOnFirstLogin, changePassword, requestPasswordReset, resetPassword, getCustomCostItems, addCustomCostItem, updateCustomCostItem, deleteCustomCostItem, getCostVariables, addCostVariable, updateCostVariable, deleteCostVariable, getCalculationRules, getActiveCalculationRule, createCalculationRule, updateCalculationRule, deleteCalculationRule, getVariableGroups, createVariableGroup, updateVariableGroup, deleteVariableGroup, getUploadSignature, deleteImageFromCloudinary, updatePropertyImages, updateEmailConfig, previewPDF, getPdfVersionHistory, getPdfVersion, restorePdfVersion, createProperty, updatePropertyDetails, deleteProperty, checkServerHealth };